"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "Was ist Reiki und wie funktioniert es?",
  answer: "Reiki ist eine traditionelle japanische Form der achtsamen Energiearbeit. Rei steht für Geist bzw. Seele, Ki für Lebensenergie. Durch sanftes Auflegen der Hände über den Energiezentren des Körpers wird der natürliche Energiefluss angeregt und harmonisiert, sodass Körper, Geist und Emotionen wieder ins Gleichgewicht finden können."
}, {
  id: "item-2",
  question: "Was sind die 5 Reiki-Lebensregeln?",
  answer: "Die von Reiki-Begründer Usui Mikao überlieferten fünf Lebensregeln lauten: Sei nicht ärgerlich. Sorge Dich nicht. Sei dankbar. Sei ehrlich in Deiner Arbeit. Sei liebevoll zu Dir selbst und zu anderen Lebewesen. Sie gelten als achtsame Grundhaltung für den Alltag, nicht als starre Vorschrift."
}, {
  id: "item-3",
  question: "Wie seriös ist Reiki?",
  answer: "Reiki wird international als ergänzende, komplementäre Methode zur Entspannung und zum Stressabbau eingesetzt. Bei Wandelherz wird Reiki professionell, geerdet und ohne esoterische Übertreibungen vermittelt. Wichtig: Reiki ersetzt keine ärztliche oder psychotherapeutische Behandlung, sondern ergänzt sie achtsam."
}, {
  id: "item-4",
  question: "Kann Reiki Blockaden lösen?",
  answer: "Reiki kann dabei unterstützen, energetische und emotionale Blockaden sanft zu lösen, indem der natürliche Energiefluss im Körper wieder angeregt wird. Viele Klientinnen und Klienten berichten nach einer Sitzung von einem Gefühl der Leichtigkeit und emotionalen Klarheit."
}, {
  id: "item-5",
  question: "Was macht eine Reiki-Behandlung?",
  answer: "Eine Reiki-Behandlung wirkt über sanftes Handauflegen entlang der Energiezentren des Körpers. Dabei wird das vegetative Nervensystem beruhigt, Verspannungen dürfen sich lösen und der natürliche Energiefluss wird harmonisiert für tiefe Entspannung und neue Kraft."
}, {
  id: "item-6",
  question: "Was passiert im Körper bei Reiki?",
  answer: "Während einer Sitzung kann sich das vegetative Nervensystem tief entspannen. Viele Menschen spüren angenehme Wärme, ein leichtes Kribbeln oder ein spürbares Loslassen von Anspannung, während der Körper in einen Zustand der Ruhe und Regeneration findet."
}, {
  id: "item-7",
  question: "Wie läuft eine Reiki-Behandlung ab?",
  answer: "Nach einem einfühlsamen Erstgespräch legst Du Dich bekleidet und bequem auf die Behandlungsliege, während sanfte Klänge und eine bewusste Atemübung die Entspannung einleiten. Durch behutsames Handauflegen entlang der Energiezentren wird die Behandlung durchgeführt. Zum Abschluss verweilst Du kurz in Stille, bevor gemeinsam Wahrnehmungen und Impulse für den Alltag besprochen werden."
}, {
  id: "item-8",
  question: "Was kann man mit Reiki heilen?",
  answer: "Reiki wird häufig begleitend bei Stress, Erschöpfung, Schlafstörungen, emotionalen Blockaden, innerer Unruhe sowie körperlichen Verspannungen eingesetzt und wirkt unterstützend auf das allgemeine Wohlbefinden. Es ersetzt jedoch keine medizinische oder psychotherapeutische Behandlung."
}];
export default function ReikiFaq() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="reikifaq" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeUpVariant} className="text-center mb-12 md:mb-16 flex flex-col items-center">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-medium tracking-wide uppercase bg-secondary text-secondary-foreground rounded-full border border-border">
            Häufige Fragen
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight text-foreground">
            Reiki FAQ
          </h2>

          <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-xl">
            Transparente Antworten auf die wichtigsten Fragen rund um Ablauf,
            Wirkung und Haltung der achtsamen Energiearbeit.
          </p>
        </motion.div>

        {/* Accordion List */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUpVariant} className="bg-card border border-border rounded-xl shadow-xs p-4 sm:p-8">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                <AccordionItem value={item.id} className="border-b border-border py-1 last:border-b-0">
                  <AccordionTrigger className="text-left font-serif text-base sm:text-lg text-foreground hover:no-underline hover:text-primary transition-colors py-4">
                    <span>{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        {/* Call to action footer */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeUpVariant} className="mt-12 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <HelpCircle className="size-4 text-primary" />
            <span>Hast Du eine weitere persönliche Frage?</span>
          </div>
          <Button asChild variant="default" className="rounded-full px-6">
            <Link to="/ContactPage">
              <span>Kontakt aufnehmen</span>
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}